import { Fragment } from "react";
import { Link } from "react-router-dom";
import { aiChatContent } from "../content/ai-chat.js";

function IconEmbed() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 20h8M12 18v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconKnowledge() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M6 5h12v14H6V5Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M9 9h6M9 12h6M9 15h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconBusiness() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <ellipse cx="12" cy="6" rx="7" ry="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M5 6v6c0 1.7 3.1 3 7 3s7-1.3 7-3V6M5 12v6c0 1.7 3.1 3 7 3s7-1.3 7-3v-6"
        stroke="currentColor"
        strokeWidth="1.6"
      />
    </svg>
  );
}

function IconSecure() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3 5 6v6c0 4.2 3 7.4 7 9 4-1.6 7-4.8 7-9V6l-7-3Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M9.5 12.5 11.5 14.5 15.5 10.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

const FEATURE_ICONS = {
  embed: IconEmbed,
  knowledge: IconKnowledge,
  business: IconBusiness,
  secure: IconSecure,
};

export default function AiChatPage() {
  const c = aiChatContent;

  return (
    <article className="site-page aichat-page">
      <div className="site-page__inner site-page__inner--wide aichat-page__inner">
        <header className="aichat-hero">
          <p className="site-page__eyebrow">{c.eyebrow}</p>
          <h1 className="site-page__title aichat-hero__title">{c.title}</h1>
          <p className="site-page__lead aichat-hero__lead">{c.lead}</p>
          <ul className="aichat-hero__points">
            {c.heroPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
          <p className="site-page__cta aichat-hero__cta">
            <Link to="/contact" className="site-button-primary">
              {c.cta.primary}
            </Link>
            <Link to="/pricing" className="site-button-ghost">
              {c.cta.secondary}
            </Link>
          </p>
        </header>

        <section className="aichat-features" aria-labelledby="aichat-features-heading">
          <h2 id="aichat-features-heading" className="aichat-section-title">
            Built around your business
          </h2>
          <div className="aichat-features__grid">
            {c.features.map((f) => {
              const Icon = FEATURE_ICONS[f.icon] ?? IconEmbed;
              return (
                <article key={f.id} className="aichat-feature-card">
                  <span className="aichat-feature-card__icon" aria-hidden="true">
                    <Icon />
                  </span>
                  <h3>{f.title}</h3>
                  <p>{f.body}</p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="aichat-platforms" aria-labelledby="aichat-platforms-heading">
          <h2 id="aichat-platforms-heading" className="aichat-section-title">
            {c.platforms.title}
          </h2>
          <p className="aichat-platforms__body">{c.platforms.body}</p>
          <ul className="aichat-platforms__chips" aria-label="Supported site types">
            {c.platforms.chips.map((chip) => (
              <li key={chip}>{chip}</li>
            ))}
          </ul>
        </section>

        <section className="aichat-models" aria-labelledby="aichat-models-heading">
          <h2 id="aichat-models-heading" className="aichat-section-title">
            {c.models.title}
          </h2>
          <p className="aichat-models__body">{c.models.body}</p>
          <div className="aichat-flow" aria-hidden="true">
            {c.models.options.map((opt, i) => (
              <Fragment key={opt.label}>
                {i > 0 ? <span className="aichat-flow__or">or</span> : null}
                <div className="aichat-flow__node">
                  <span className="aichat-flow__label">{opt.label}</span>
                  <span className="aichat-flow__hint">{opt.hint}</span>
                </div>
              </Fragment>
            ))}
          </div>
        </section>

        <section className="aichat-access" aria-labelledby="aichat-access-heading">
          <h2 id="aichat-access-heading" className="aichat-section-title">
            {c.access.title}
          </h2>
          <p className="aichat-access__body">{c.access.body}</p>
          <ul className="aichat-access__points">
            {c.access.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        </section>

        <section
          className="aichat-dashboard-preview"
          aria-labelledby="aichat-dashboard-heading"
        >
          <h2 id="aichat-dashboard-heading" className="aichat-section-title">
            {c.analytics.title}
          </h2>
          <p className="aichat-dashboard-preview__subtitle">{c.analytics.subtitle}</p>

          <div className="aichat-dash" role="img" aria-label="Sample AI chat analytics dashboard">
            <div className="aichat-dash__top">
              <div>
                <p className="aichat-dash__eyebrow">Analytics</p>
                <p className="aichat-dash__business">{c.analytics.businessName}</p>
              </div>
              <span className="aichat-dash__live">
                <span className="aichat-dash__live-dot" />
                {c.analytics.liveBadge}
              </span>
            </div>

            <div className="aichat-dash__metrics">
              <div className="aichat-dash__metric">
                <span className="aichat-dash__metric-value">
                  {c.analytics.metrics.conversations}
                </span>
                <span className="aichat-dash__metric-label">Conversations</span>
              </div>
              <div className="aichat-dash__metric aichat-dash__metric--highlight">
                <span className="aichat-dash__metric-value">
                  {c.analytics.metrics.resolved}
                </span>
                <span className="aichat-dash__metric-label">Resolved without handoff</span>
              </div>
              <div className="aichat-dash__metric">
                <span className="aichat-dash__metric-value">
                  {c.analytics.metrics.avgResponse}
                </span>
                <span className="aichat-dash__metric-label">Typical response time</span>
              </div>
            </div>

            <div className="aichat-dash__panels">
              <div className="aichat-dash__panel">
                <h3>Active right now</h3>
                <ul className="aichat-dash__active-list">
                  {c.analytics.activeChats.map((chat) => (
                    <li key={chat.id} className="aichat-dash__active-item">
                      <div className="aichat-dash__active-head">
                        <strong>{chat.id}</strong>
                        <span className="aichat-dash__pill">{chat.state}</span>
                      </div>
                      <p>{chat.snippet}</p>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="aichat-dash__panel">
                <h3>Recent conversations</h3>
                <div className="aichat-dash__table-wrap">
                  <table className="aichat-dash__table">
                    <thead>
                      <tr>
                        <th scope="col">Session</th>
                        <th scope="col">Topic</th>
                        <th scope="col">Outcome</th>
                        <th scope="col">What happened</th>
                        <th scope="col">When</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.analytics.recent.map((row) => (
                        <tr key={row.id}>
                          <td>{row.id}</td>
                          <td>{row.topic}</td>
                          <td>
                            <span className="aichat-dash__outcome">{row.outcome}</span>
                          </td>
                          <td>{row.response}</td>
                          <td className="aichat-dash__time">{row.time}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        <p className="site-page__cta">
          <Link to="/contact" className="site-button-primary">
            {c.cta.primary}
          </Link>
          <Link to="/" className="site-button-ghost">
            ← Back to home
          </Link>
        </p>
      </div>
    </article>
  );
}
